import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { AccountColumns, AccountData } from '@juvare/contact-management/backend/features/accounts/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { requestLicenseCheckFunctionDef } from '@juvare/credentialing/backend/features/credentialing-verification/shared';
import { allowRequestLicenseCheck } from '@juvare/credentialing/backend/features/permissions';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { GetResponse } from '@juvare/platform-shared/features/storage';
import { useTranslation } from 'react-i18next';
import { SlideoutMenuItem } from './useSlideout';

export function useCredentialDetailsConfig(): {
    getMenuItems: (account: GetResponse<AccountData, AccountColumns>, reloadAccounts?: () => void) => SlideoutMenuItem[];
} {
    const { t } = useTranslation();
    const userState = useUserState();
    const requestLicenseCheck = useTypedPlatformFunction(requestLicenseCheckFunctionDef);

    return {
        getMenuItems: (account, reloadAccounts): SlideoutMenuItem[] => {
            const items: SlideoutMenuItem[] = [];
            const isAccountActive = account.data.systemStatus === 'active';
            const canRequestLicenseCheck = allowRequestLicenseCheck(userState) && isAccountActive;

            if (canRequestLicenseCheck) {
                items.push({
                    label: t('menuItems.verifyCredentials'),
                    onClick: () => {
                        showConfirmDialog({
                            title: t('credentialing.titles.verifyCredentials'),
                            onConfirm: () => {
                                requestLicenseCheck({
                                    personId: account.id,
                                    requestedBy: userState?.displayName,
                                })
                                    .then(() => {
                                        showInfoSnackbar({ message: t('credentialing.messages.licenseCheckRequested') });
                                    })
                                    .catch(handleErrorDialog)
                                    .finally(() => {
                                        reloadAccounts && reloadAccounts();
                                    });
                            },
                        });
                    },
                    'data-test': 'menu-item-license-check',
                });
            }

            return items;
        },
    };
}
